import { MetricDto, MetricValueDto } from "dtos/dtos";

/**
 * All metric keys.
 */
export const METRIC_KEY = {
  wPersHw: "wpershw",
  wPersHwBest: "wpershw_best",
  popInc: "pop_inc",
  popDec: "pop_dec",
  popTh: "pop_th",
  populationVariation: "population_variation",
  populationTemporary: "population_temporary",
  populationPending: "population_pending",
  populationRelevant: "population_relevant",
  populationPotCovered: "population_pot_covered",
  populationPotTotalCovered: "population_pot_total_covered",
  populationPotUncovered: "population_pot_uncovered",
  populationCapacityCovered: "population_capacity_covered",
  populationCapacityUncovered: "population_capacity_uncovered",
  populationCapacity: "population_capacity",
  populationCorrection: "population_correction",
  wohnflEinw: "wohnfl_einw",
  wohnflBest: "wohnfl_best",
  wohnflImbBew: "wohnfl_imb_bew",
  areaRealizedCovered: "area_realized_covered", // [m^2]
  areaPotCovered: "area_pot_covered", // [m^2]
  areaPotUncovered: "area_pot_uncovered", // [m^2]
  fBebaut: "f_bebaut", // [ha]
  fUnbebaut: "f_unbebaut", // [ha]
  fTotal: "f_total", // [ha]
  wohnWnart3010Best: "wohn_wnart_3010_best",
  wohnWnart3010Best0E: "wohn_wnart_3010_best_0E",
  wohnWnart3010ImbBew: "wohn_wnart_3010_imb_bew",
  wohnWnart3020Best: "wohn_wnart_3020_best",
  wohnWnart3020ImbBew: "wohn_wnart_3020_imb_bew",
  wohnWnart3030Best: "wohn_wnart_3030_best",
  wohnWnart3030ImbBew: "wohn_wnart_3030_imb_bew",
  wohnWnart3031Best: "wohn_wnart_3031_best",
  wohnWnart3031ImbBew: "wohn_wnart_3031_imb_bew",
  wohnWnart3032Best: "wohn_wnart_3032_best",
  wohnWnart3032ImbBew: "wohn_wnart_3032_imb_bew",
  wohnWnart3033Best: "wohn_wnart_3033_best",
  wohnWnart3033ImbBew: "wohn_wnart_3033_imb_bew",
  wohnWnart3034Best: "wohn_wnart_3034_best",
  wohnWnart3034ImbBew: "wohn_wnart_3034_imb_bew",
  wohnWnart3035Best: "wohn_wnart_3035_best",
  wohnWnart3035ImbBew: "wohn_wnart_3035_imb_bew",
  wohnWnart3036Best: "wohn_wnart_3036_best",
  wohnWnart3036ImbBew: "wohn_wnart_3036_imb_bew",
  wohnWnart3037Best: "wohn_wnart_3037_best",
  wohnWnart3037ImbBew: "wohn_wnart_3037_imb_bew",
  wohnWnart3038Best: "wohn_wnart_3038_best",
  wohnWnart3038ImbBew: "wohn_wnart_3038_imb_bew",
  wohnWnart3070Best: "wohn_wnart_3070_best",
  wohnWnart3070ImbBew: "wohn_wnart_3070_imb_bew",
  wohnWnartNullBest: "wohn_wnart_null_best",
  wohnWnartNullImbBew: "wohn_wnart_null_imb_bew",
  wohnant: "wohnant",
  az: "az",
  uez: "uez",
  varUezFinal: "var_uez_final",
  avgBuildingHeight: "avg_building_height",
  avgBuildingHeightFinal: "avg_building_height_final",
  fahoeMax: "fahoe_max",
  geshoe: "geshoe",
  varHabitationRatioFinal: "var_habitation_ratio_final",
  lBuildingAreaDemandActual: "L_building_area_demand_actual",
  mBuildingAreaDemandExpected: "M_building_area_demand_expected",
  bPopulationCapacityTotal: "B_population_capacity_total",
  populationKgwrFinal: "population_kgwr_final",
  cPopulationGrowth: "C_population_growth",
  ePopulationGrowthAnnual: "E_population_growth_annual",
  qPopulationFuture: "Q_population_future",
  rPopulationNewZonesDemand: "R_population_new_zones_demand",
  pVarFactorRp: "P_var_factor_rp",
  dPopulationGrowthPercent: "D_population_growth_percent",
  fPopulationGrowthAnnualPercent: "F_population_growth_annual_percent",
  oBuildingAreaDemandComparison: "O_building_area_demand_comparison",
  aPopulationActual: "A_population_actual",
  populationHist: "population_hist",
  compensation: "compensation",
};

/**
 * Represents the level on which a metric value is calculated.
 */
export type MetricValueLevel =
  | {
      name: "districtZoneGde";
      zonTypGde: number;
      zonTypKt: number;
      zonTypPbg: number;
      bfsNr1999: number;
      bfsNr: number;
    }
  | {
      name: "zonTypPbg";
      zonTypPbg: number;
      bfsNr: number;
    }
  | {
      name: "districtResidential"; // Level district with zones PBG <= 3000
      bfsNr1999: number;
      bfsNr: number;
    }
  | {
      name: "district";
      bfsNr1999: number;
      bfsNr: number;
    }
  | {
      name: "municipality";
      bfsNr: number;
    }
  | {
      name: "canton";
    };

/**
 * Defines to which metric a value belongs and on which level it is calculated.
 * A context uniquely identifies a metric value and can be used to hash it.
 */
export class MetricValueContext {
  metricKey: string;
  level: MetricValueLevel;

  constructor(metricKey: string, level: MetricValueLevel) {
    this.metricKey = metricKey;
    this.level = level;
  }

  getHash(): string {
    let hash = "";

    switch (this.level.name) {
      case "districtZoneGde":
        hash = [
          this.metricKey,
          this.level.bfsNr,
          this.level.bfsNr1999,
          this.level.zonTypGde,
        ].join("-");
        break;
      case "districtResidential":
        hash = [
          this.metricKey,
          this.level.bfsNr,
          this.level.bfsNr1999,
          "residential",
        ].join("-");
        break;
      case "district":
        hash = [this.metricKey, this.level.bfsNr, this.level.bfsNr1999].join(
          "-"
        );
        break;
      case "zonTypPbg":
        hash = [this.metricKey, this.level.bfsNr, this.level.zonTypPbg].join(
          "-"
        );
        break;
      case "municipality":
        hash = [this.metricKey, this.level.bfsNr].join("-");
        break;
      case "canton":
        hash = this.metricKey;
        break;
    }

    return hash;
  }
}

/**
 * A container for fast retrieval of metrics.
 */
export class Metrics {
  metricRecord: Record<string, MetricDto>;

  constructor(metrics: MetricDto[]) {
    this.metricRecord = this.createMetricRecord(metrics);
  }

  getMetric(metricKey: string): MetricDto | undefined {
    return this.metricRecord.hasOwnProperty(metricKey)
      ? this.metricRecord[metricKey]
      : undefined;
  }

  private createMetricRecord(metrics: MetricDto[]): Record<string, MetricDto> {
    return metrics.reduce<Record<string, MetricDto>>((result, metric) => {
      result[metric.key] = metric;
      return result;
    }, {});
  }
}

/**
 * Represents a metric value. A value is bound to a context.
 */
export class MetricValue {
  value: number;
  context: MetricValueContext;

  constructor(value: number, context: MetricValueContext) {
    this.value = value;
    this.context = context;
  }

  getHash(): string {
    return this.context.getHash();
  }
}

/**
 * A container for managing all metric values.
 */
export class MetricValues {
  private sourceValues: MetricValueDto[];
  private valueRecord: Record<string, MetricValue>;

  constructor(sourceValues: MetricValueDto[]) {
    this.sourceValues = sourceValues;
    this.valueRecord = {};
  }

  /**
   * Aggregates the source values over "zone shields"
   */
  aggregateZoneGde(metrics: Metrics) {
    this.sourceValues.reduce<
      Record<
        string,
        {
          value: number;
          n: number;
        }
      >
    >((result, metricValueDto) => {
      const metric = metrics.getMetric(metricValueDto.metricKey);
      const context = new MetricValueContext(metricValueDto.metricKey, {
        name: "districtZoneGde",
        zonTypGde: metricValueDto.zonTypGde,
        zonTypKt: metricValueDto.zonTypKt,
        zonTypPbg: metricValueDto.zonTypPbg,
        bfsNr1999: metricValueDto.bfsNr1999,
        bfsNr: metricValueDto.bfsNr,
      });
      const key = context.getHash();

      if (metric !== undefined) {
        if (result.hasOwnProperty(key)) {
          let newValue = 0;
          const oldValue = result[key].value;
          const oldN = result[key].n;
          const newN = oldN + 1;

          if (metric.aggregationType === "sum") {
            newValue = oldValue + metricValueDto.value;
          } else if (metric.aggregationType === "avg") {
            newValue = oldValue + (metricValueDto.value - oldValue) / newN;
          }

          result[key].value = newValue;
          result[key].n = newN;
        } else {
          result[key] = {
            value: metricValueDto.value,
            n: 1,
          };
        }

        this.updateOrAddValue(new MetricValue(result[key].value, context));
      }

      return result;
    }, {});
  }

  /**
   * Aggregate the metric values over the given level
   */
  aggregate(
    metrics: Metrics,
    level:
      | "districtResidential"
      | "district"
      | "zonTypPbg"
      | "municipality"
      | "canton"
  ) {
    const levelFilter =
      level === "districtResidential" ||
      level === "district" ||
      level === "zonTypPbg"
        ? "districtZoneGde"
        : "municipality"
        ? "district"
        : "municipality";

    this.getAllValues()
      .filter((v) => v.context.level.name === levelFilter)
      .reduce<
        Record<
          string,
          {
            value: number;
            n: number;
          }
        >
      >((result, metricValue) => {
        if (
          (level === "districtResidential" &&
            metricValue.context.level.name === "districtZoneGde" &&
            metricValue.context.level.zonTypPbg <= 3000) ||
          level !== "districtResidential"
        ) {
          let context = undefined;

          switch (level) {
            case "districtResidential":
              context = new MetricValueContext(metricValue.context.metricKey, {
                name: level,
                bfsNr1999: (metricValue.context.level as any).bfsNr1999,
                bfsNr: (metricValue.context.level as any).bfsNr,
              });
              break;

            case "district":
              context = new MetricValueContext(metricValue.context.metricKey, {
                name: level,
                bfsNr1999: (metricValue.context.level as any).bfsNr1999,
                bfsNr: (metricValue.context.level as any).bfsNr,
              });
              break;

            case "zonTypPbg":
              context = new MetricValueContext(metricValue.context.metricKey, {
                name: level,
                zonTypPbg: (metricValue.context.level as any).zonTypPbg,
                bfsNr: (metricValue.context.level as any).bfsNr,
              });
              break;

            case "municipality":
              context = new MetricValueContext(metricValue.context.metricKey, {
                name: level,
                bfsNr: (metricValue.context.level as any).bfsNr,
              });
              break;
            case "canton":
              context = new MetricValueContext(metricValue.context.metricKey, {
                name: level,
              });
          }

          const metric = metrics.getMetric(metricValue.context.metricKey);

          if (metric !== undefined) {
            const key = context.getHash();

            if (result.hasOwnProperty(key)) {
              let newValue = 0;
              const oldValue = result[key].value;
              const oldN = result[key].n;
              const newN = oldN + 1;

              if (metric.aggregationType === "sum") {
                newValue = oldValue + metricValue.value;
              } else if (metric.aggregationType === "avg") {
                newValue = oldValue + (metricValue.value - oldValue) / newN;
              }

              result[key].value = newValue;
              result[key].n = newN;
            } else {
              result[key] = {
                value: metricValue.value,
                n: 1,
              };
            }

            this.updateOrAddValue(new MetricValue(result[key].value, context));
          }
        }

        return result;
      }, {});
  }

  /**
   * Get the metric value given a context
   */
  getValue(context: MetricValueContext): MetricValue | undefined {
    const key = context.getHash();

    return this.valueRecord.hasOwnProperty(key)
      ? this.valueRecord[key]
      : undefined;
  }

  /**
   * Gets all metric values
   */
  getAllValues(): MetricValue[] {
    return Object.values(this.valueRecord);
  }

  /**
   * Returns the source metric values aka Q1
   */
  getSourceValues(): MetricValueDto[] {
    return this.sourceValues;
  }

  /**
   * Adds the given metric value and overwrites an existing one with the same context.
   */
  addValue(metricValue: MetricValue): void {
    const key = metricValue.getHash();
    this.valueRecord[key] = metricValue;
  }

  /**
   * Updates the value of the given metric value or adds it
   * if it doesn't exist.
   */
  updateOrAddValue(metricValue: MetricValue): void {
    const key = metricValue.getHash();

    if (this.valueRecord.hasOwnProperty(key)) {
      this.valueRecord[key].value = metricValue.value;
    } else {
      this.addValue(metricValue);
    }
  }
}
